import { useEffect, useState } from 'react';

import { SERVER_ORIGIN } from '../config';

// Landing page hit from the email link. Reads ?token=... from the URL and
// fires GET /auth/verify-email — server flips the verified flag and we show
// the outcome. After ~3s of success state, we drop the URL back to '/' so a
// reload doesn't replay the (now consumed) token.
export function VerifyEmailPage() {
  // Parse the token once at mount via the state initializer (avoids the
  // "setState directly in effect" lint warning for the no-token branch).
  const [token] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  });
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>(token ? 'loading' : 'error');
  const [message, setMessage] = useState<string>(token ? '' : 'Missing verification token.');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SERVER_ORIGIN}/auth/verify-email?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.ok) {
          setStatus('ok');
          setMessage('Your email has been verified. Welcome aboard.');
          // Clear the token from history after a beat so reload won't 400.
          setTimeout(() => {
            window.history.replaceState({}, '', '/');
            window.location.reload();
          }, 2500);
        } else {
          const j = await res.json().catch(() => ({}));
          setStatus('error');
          setMessage(j.error ?? 'Verification failed.');
        }
      } catch {
        if (cancelled) return;
        setStatus('error');
        setMessage('Network error. Try again later.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div style={pageStyle}>
      <h1 style={{ color: 'var(--accent-color)', marginBottom: 8, fontSize: '2.5rem' }}>GrandSnatch</h1>
      <h2 style={{ marginBottom: 24, fontSize: '1rem', color: '#aaa' }}>Email verification</h2>
      <div style={{
        maxWidth: 420, padding: 20, background: '#15151f', border: '1px solid #2a2a3a',
        borderRadius: 6, textAlign: 'center',
      }}>
        {status === 'loading' && <div style={{ color: '#bbb' }}>Verifying...</div>}
        {status === 'ok' && (
          <>
            <div style={{ fontSize: 36, marginBottom: 8 }}>✓</div>
            <div style={{ color: '#4CAF50', fontWeight: 'bold', marginBottom: 6 }}>{message}</div>
            <div style={{ color: '#888', fontSize: 12 }}>Redirecting...</div>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: 36, marginBottom: 8 }}>✗</div>
            <div style={{ color: '#e57373', marginBottom: 12 }}>{message}</div>
            <a href="/" style={{ color: '#4fc3f7', fontSize: 13 }}>Back to the app</a>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  height: '100vh', background: 'var(--bg-color)', color: 'var(--text-color)',
};
